import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery, useTheme } from '@mui/material';
import { bindActionCreators } from 'redux';

import { selectBookingsByEventId } from '~/store/bookings';
import { BookingStatus } from '~/Models/DTO/Booking.models';
import { requestBookingListForEvent, requestUpdateBookings } from '~/store';
import { Segment } from '~/Helpers/Segment.helper';
import {
  deleteSingleEvent,
  requestSingleEvent,
  requestUpdateEvent,
  selectEventById,
} from '~/store/events';
import { Route } from '~/Constants/Routes.constants';
import { routeReplacer } from '~/Helpers/String.helper';
import { useNavigation, useProvider } from '~/Hooks';

export const useEventPage = () => {
  const dispatch = useDispatch();
  const { getParams, navigate, goBack } = useNavigation();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isUsingThePartnerTool } = useProvider();

  const { domain, id } = getParams();

  const selectedEvent = useSelector(selectEventById(id || ''));
  const bookings = useSelector(selectBookingsByEventId(selectedEvent?.id));

  const loadSingleEvent = bindActionCreators(requestSingleEvent, dispatch);
  const eventUpdate = bindActionCreators(requestUpdateEvent, dispatch);
  const deleteEvent = bindActionCreators(deleteSingleEvent, dispatch);
  const bookingsUpdate = bindActionCreators(requestUpdateBookings, dispatch);
  const reloadBookings = bindActionCreators(requestBookingListForEvent, dispatch);

  const [isUncancelModalOpen, setIsUncancelModalOpen] = useState(false);
  const [isDeleteEventModalOpen, setIsDeleteEventModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const title = isMobile ? selectedEvent?.type : selectedEvent?.name;

  useEffect(() => {
    if (id) {
      loadSingleEvent(id);
    }
  }, []);

  const handleUncancelEvent = () => {
    setIsUncancelModalOpen(false);
    eventUpdate({
      ...selectedEvent,
      cancelled: false,
      cancellationReason: null,
      sendEmail: false,
    });
    Segment.track('click_on_edit_event_uncancel_save');
  };

  const handleDeleteEvent = () => {
    setIsDeleteEventModalOpen(false);
    if (id) {
      deleteEvent(id);
    }
    goBack();
  };

  const handleCancelEvent = (sendMail: boolean, reason: string, shouldUpdateEvent = true) => {
    setIsCancelModalOpen(false);

    const cancelRequest = Object.values(bookings).map((booking) => ({
      bookingId: booking.id,
      orderLineId: booking.orderLine?.id,
      presenceType: booking.presenceType,
      bookingStatus: BookingStatus.CANCELLED,
      sendMail: true,
      reason,
      refund: true,
      origin: 'NeoPro',
      force: true,
    }));
    if (cancelRequest.length) {
      bookingsUpdate(cancelRequest);
    }
    if (shouldUpdateEvent) {
      eventUpdate({
        ...selectedEvent,
        cancelled: true,
        cancellationReason: reason,
        sendEmail: sendMail,
      });

      if (cancelRequest.length) {
        reloadBookings({ eventId: selectedEvent.id, domain: domain || '' });
      }
    }
    Segment.track('click_on_edit_event_cancel_save');
  };

  const handleEdit = () =>
    navigate(
      routeReplacer(Route.EVENT_EDIT, {
        domain: domain || '',
        id: id || '',
        eventType: selectedEvent.type,
      }),
    );

  const handleOpenUncancel = (isOpen: boolean) => {
    setIsUncancelModalOpen(isOpen);
    if (isOpen) {
      Segment.track('click_on_edit_event_uncancel');
    }
  };

  const handleOpenDelete = (isOpen: boolean) => setIsDeleteEventModalOpen(isOpen);

  const handleOpenCancel = (isOpen: boolean) => setIsCancelModalOpen(isOpen);

  const options = selectedEvent?.cancelled
    ? [
        {
          label: t('app.uncancel'),
          onClick: () => handleOpenUncancel(true),
        },
        {
          label: t('app.delete'),
          onClick: () => handleOpenDelete(true),
        },
      ]
    : [
        {
          label: t('app.edit'),
          onClick: handleEdit,
        },
        {
          label: t('app.cancel_event'),
          onClick: () => handleOpenCancel(true),
        },
      ];

  return {
    selectedEvent,
    title,
    isUsingThePartnerTool,
    modalsOpen: {
      uncancel: isUncancelModalOpen,
      delete: isDeleteEventModalOpen,
      cancel: isCancelModalOpen,
    },
    modalConfirmHandler: {
      uncancel: handleUncancelEvent,
      delete: handleDeleteEvent,
      cancel: handleCancelEvent,
    },
    handleOpenUncancel,
    handleOpenDelete,
    handleOpenCancel,
    options,
  };
};
